import { useEffect, useState } from 'react';
import Widget from '../../../components/Dashboard/Widget';
import { useDashboardControls } from 'contexts/Dashboard/useDashboardControls';
import {
  Area,
  AreaChart,
  Brush,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { KPIsWidgetEnum, LongTitles } from '../utils';
import { orange } from 'constants/Colors';
import { formatDate } from 'utils/date-helpers';
import { useKPIGetTB } from 'api/kpi/kpi';
import { KPITBDataPoint } from 'api/model';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faSpinner } from '@fortawesome/free-solid-svg-icons';
import Button from 'components/inputs/Button';
import { useExportExportTBHook } from 'api/export/export';
import { showException } from 'utils/exception-helper';
import { downloadBlob } from 'utils/file-utils';

const TBWidget = () => {
  const [lineData, setLineData] = useState<KPITBDataPoint[]>([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const {
    dashboardState: {
      daterange,
      selectedCaseTypes,
      selectedSupplierIDs,
      selectedCustomerNames,
      selectedFromCountyIDs,
      selectedToCountyIDs,
      excludeCompanyNameContains,
    },
  } = useDashboardControls();

  const getKPITBData = useKPIGetTB(
    {
      startDate: daterange[0]!,
      endDate: daterange[1]!,
      caseTypes: selectedCaseTypes,
      supplierIDs: selectedSupplierIDs,
      customerNames: selectedCustomerNames,
      fromCountyIDs: selectedFromCountyIDs,
      toCountyIDs: selectedToCountyIDs,
      excludeCompanyNameContains: excludeCompanyNameContains ?? undefined,
    },
    {
      query: {
        enabled: !!(daterange[0] && daterange[1]),
      },
    }
  );

  useEffect(() => {
    if (getKPITBData.data) {
      const groupedByDate = getKPITBData.data.dataPoints.reduce(
        (acc, item) => {
          const dateKey = item.date.toISOString().split('T')[0];
          if (!acc[dateKey]) {
            acc[dateKey] = {
              date: item.date,
              totalCost: 0,
              totalPrice: 0,
              totalProfit: 0,
              totalCases: 0,
              totalAssignments: 0,
              caseType: item.caseType,
              // Dagens summa över alla län.
              countyName: '',
            };
          }

          acc[dateKey].totalCost += item.totalCost;
          acc[dateKey].totalPrice += item.totalPrice;
          acc[dateKey].totalProfit += item.totalProfit;
          acc[dateKey].totalCases += item.totalCases;
          acc[dateKey].totalAssignments += item.totalAssignments;
          return acc;
        },
        {} as { [date: string]: KPITBDataPoint }
      );

      setLineData(
        Object.values(groupedByDate).sort(
          (a, b) => a.date.getTime() - b.date.getTime()
        )
      );
      // console.log('TB data:', getKPITBData.data.failedCases);

      // console.log('TB2 data:', getKPT_TB_Data.data);
    }
  }, [getKPITBData.data]);

  const downloadExcel = useExportExportTBHook();

  const calculateGradientOffset = () => {
    if (lineData.length === 0) {
      return 0;
    }
    // Calculate the maximum and minimum values of totalProfit
    // to determine the gradient color offset
    const { min: dataMin, max: dataMax } = lineData.reduce(
      (acc, item) => {
        const profit = item.totalProfit;
        if (profit < acc.min) {
          acc.min = profit;
        }
        if (profit > acc.max) {
          acc.max = profit;
        }
        return acc;
      },
      { min: 0, max: 0 } as {
        min: number;
        max: number;
      }
    );

    // If all values are negative, return 0
    if (dataMax <= 0) {
      return 0;
    }
    // If all values are positive, return 1
    if (dataMin >= 0) {
      return 1;
    }

    // Calculate the offset based on the range of values
    return dataMax / (dataMax - dataMin);
  };

  const gradientOffsetValue = calculateGradientOffset();

  return (
    <Widget
      title={LongTitles[KPIsWidgetEnum.TB]}
      count={undefined}
      isLoading={getKPITBData.isLoading}
      info="Täckningsbidrag (TB) är intäkter minus rörliga kostnader.
        Lönekostnad i beräkningen är 227kr/h och OB 45kr/h om inte fälttestaren tillhör en enhet med andra inställningar. 
        Kostnad inkluderar beräknad lönekostnad + alla utgifter som är kopplade till ärendet."
    >
      {/* TODO: Ta bort kommentarerna allteftersom vi implementerar mer till fullo, eller flytta till info-tooltipen */}
      <p style={{ fontWeight: 'bold', textAlign: 'center' }}>
        OBS: Datumen utgår från ärendets stängd-datum.
      </p>

      <Button
        disabled={isDownloading || lineData.length === 0}
        onClick={async () => {
          try {
            setIsDownloading(true);
            const result = await downloadExcel({
              startDate: daterange[0]!,
              endDate: daterange[1]!,
              caseTypes: selectedCaseTypes,
              supplierIDs: selectedSupplierIDs,
              customerNames: selectedCustomerNames,
              fromCountyIDs: selectedFromCountyIDs,
              toCountyIDs: selectedToCountyIDs,
              excludeCompanyNameContains:
                excludeCompanyNameContains ?? undefined,
            });
            if (result) {
              downloadBlob(
                result,
                `Nyckeltal_TB_${formatDate(daterange[0]!)}_${formatDate(daterange[1]!)}.xlsx`
              );
            }
          } catch (error) {
            showException(error);
          } finally {
            setIsDownloading(false);
          }
        }}
      >
        <FontAwesomeIcon icon={faDownload} /> Ladda ner
        {isDownloading ? <FontAwesomeIcon icon={faSpinner} spin /> : null}
      </Button>

      <ResponsiveContainer
        width="100%"
        height={300}
        initialDimension={{ width: 520, height: 400 }}
      >
        <AreaChart
          height={400}
          data={lineData}
          margin={{ top: 5, right: 40, left: 5, bottom: 90 }} // Increased bottom margin
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            tickFormatter={(date) => formatDate(new Date(date))}
            tickSize={lineData.length >= 7 ? 50 : 10}
            padding={{ left: 10, right: 10 }}
            angle={lineData.length >= 7 ? -80 : 0}
          />
          <YAxis />
          <Tooltip
            labelFormatter={(label) => {
              const date = new Date(label);
              return formatDate(date);
            }}
            formatter={(value: string | number, name: string, props) => {
              const payload = props && props.payload ? props.payload : {};
              // const cost = payload.totalCost;
              const price = payload.totalPrice;

              if (name === 'Antal ärenden')
                return [`${value}`, 'Antal ärenden'];

              if (
                name === 'Totalt resultat' &&
                typeof price === 'number' &&
                price !== 0
              ) {
                const percent = ((Number(value) / price) * 100).toFixed(1);
                return [`${value} kr (${percent}%)`, 'Totalt resultat'];
              }

              if (typeof price === 'number' && price !== 0) {
                const percent = ((Number(value) / price) * 100).toFixed(1);
                return [`${value} kr (${percent}%)`, String(name)];
              }

              return [`${value} kr`, name];
            }}
          />
          {/* Legend showing selected case types */}
          <Legend verticalAlign="top" height={36} />
          {/* TOOLTIP-RELATED VALUES */}
          <Area
            key="totalCases"
            type="monotone"
            dataKey="totalCases"
            stroke="#000000"
            strokeOpacity={0.01}
            dot={false}
            activeDot={false}
            isAnimationActive={false}
            legendType="none"
            name="Antal ärenden"
          />
          <Area
            type="monotone"
            dataKey="totalPrice"
            stroke="#5d9e42"
            strokeOpacity={0.01}
            dot={false}
            activeDot={false}
            legendType="none"
            fill="none"
            name="Intäkt"
          />
          <Area
            type="monotone"
            dataKey="totalCost"
            stroke="#cd4545"
            strokeOpacity={0.01}
            dot={false}
            activeDot={false}
            legendType="none"
            fill="none"
            name="Kostnad"
          />
          {/* Background-gradient for totalProfit to show + and - values */}
          <defs>
            <linearGradient id="diffColor" x1="0" y1="0" x2="0" y2="1">
              <stop
                offset={gradientOffsetValue}
                stopColor="#206c00"
                stopOpacity={0.5}
              />
              <stop
                offset={gradientOffsetValue}
                stopColor="#ad0000"
                stopOpacity={0.5}
              />
            </linearGradient>
          </defs>
          <Area
            type="monotone"
            dataKey="totalProfit"
            stroke="#039be7"
            strokeWidth={1.5}
            fill="url(#diffColor)"
            name="Totalt resultat"
          />
          <Brush
            dataKey="date"
            height={30}
            stroke={orange}
            y={250} // Move brush further down
            tickFormatter={(date) => formatDate(new Date(date))}
          />
        </AreaChart>
      </ResponsiveContainer>
      {lineData.length === 0 && (
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          {getKPITBData.isLoading ? (
            <FontAwesomeIcon icon={faSpinner} spin />
          ) : (
            <p>Ingen data att visa för vald filtrering.</p>
          )}
        </div>
      )}
    </Widget>
  );
};

export default TBWidget;
